import { useState } from 'react';

interface AccordionItemProps {
  title: string;
  number: number;
  color: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function AccordionItem({ title, number, color, isOpen, onToggle, children }: AccordionItemProps) {
  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-lg hover:shadow-xl transition-all duration-300">
      <button
        onClick={onToggle}
        className="w-full px-8 py-6 flex items-center justify-between bg-gradient-to-l from-gray-50 to-white hover:from-gray-100 hover:to-gray-50 transition-all duration-300 cursor-pointer"
      >
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-full ${color} flex items-center justify-center text-white font-bold text-xl shadow-lg`}>
            {number}
          </div>
          <h3 className="font-bold text-2xl text-gray-900">{title}</h3>
        </div>
        <div className={`w-10 h-10 flex items-center justify-center transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          <i className="ri-arrow-down-s-line text-3xl text-[#FF6B6B]"></i>
        </div>
      </button>
      <div
        className={`transition-all duration-500 ease-in-out ${
          isOpen ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
        } overflow-hidden`}
      >
        <div className="p-8 bg-white">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function GlobalModelsAppendix() {
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);

  const toggleAccordion = (id: string) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  const models = [
    {
      id: 'wework',
      title: 'WeWork',
      subtitle: 'النموذج التجاري العالمي',
      image: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&h=400&fit=crop&q=80',
      fallback: 'https://via.placeholder.com/800x400/FF6B6B/FFFFFF?text=WeWork+Model',
      year: '2010',
      location: 'نيويورك',
      stats: [
        { label: 'التأسيس', value: '2010 - نيويورك' },
        { label: 'الانتشار', value: '700+ موقع في 35+ دولة' },
        { label: 'النموذج', value: 'ربحي - Commercial Coworking' }
      ]
    },
    {
      id: 'impacthub',
      title: 'Impact Hub',
      subtitle: 'نموذج الأثر الاجتماعي',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=400&fit=crop&q=80',
      fallback: 'https://via.placeholder.com/800x400/FF8E53/FFFFFF?text=Impact+Hub+Model',
      year: '2005',
      location: 'لندن',
      stats: [
        { label: 'التأسيس', value: '2005 - لندن' },
        { label: 'الانتشار', value: '100+ مدينة حول العالم' },
        { label: 'النموذج', value: 'اجتماعي - شبكي - هجين' }
      ]
    }
  ];

  void models;

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-gradient-to-l from-[#FF6B6B] to-[#FF8E53] text-white px-6 py-2 rounded-full text-sm font-bold mb-6">
            ملحق النماذج المرجعية
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            مساحة العمل المشتركة
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            التجارب العالمية والإقليمية
          </p>
        </div>

        <div className="max-w-6xl mx-auto space-y-6">
          {/* ZINC Accordion */}
          <AccordionItem
            title="Zain Innovation Campus – ZINC (الأردن)"
            number={1}
            color="bg-gradient-to-br from-blue-500 to-blue-600"
            isOpen={openAccordion === 'zinc'}
            onToggle={() => toggleAccordion('zinc')}
          >
            <div className="space-y-8">
              {/* Hero Image */}
              <div className="w-full h-80 rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-gray-100 to-gray-200">
                <img
                  src="/ZINC1.jpg"
                  alt="ZINC - Zain Innovation Campus"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&h=400&fit=crop&q=80';
                  }}
                />
              </div>

              {/* 1. نبذة عامة */}
              <div>
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-gradient-to-br from-[#4FACFE] to-[#00A0E3] rounded-xl flex items-center justify-center text-white">1</span>
                  نبذة عامة
                </h4>
                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-100">
                    <div className="text-sm font-semibold text-gray-700 mb-2">التأسيس</div>
                    <div className="text-lg font-bold text-gray-900">2017</div>
                    <div className="text-sm text-gray-600">من قبل شركة زين الأردن</div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-100">
                    <div className="text-sm font-semibold text-gray-700 mb-2">الانتشار</div>
                    <div className="text-lg font-bold text-gray-900">عدة فروع</div>
                    <div className="text-sm text-gray-600">داخل الأردن (عمّان بشكل رئيسي)</div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-100">
                    <div className="text-sm font-semibold text-gray-700 mb-2">النموذج</div>
                    <div className="text-lg font-bold text-gray-900">Innovation Hub</div>
                    <div className="text-sm text-gray-600">مدعوم من شركة كبرى</div>
                  </div>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <h5 className="font-bold text-lg text-gray-900 mb-4">الفئة المستهدفة:</h5>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 text-gray-700">
                      <i className="ri-check-line text-[#4FACFE]"></i>
                      <span>رواد الأعمال</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <i className="ri-check-line text-[#4FACFE]"></i>
                      <span>الطلاب</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <i className="ri-check-line text-[#4FACFE]"></i>
                      <span>الجهات الناشئة</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <i className="ri-check-line text-[#4FACFE]"></i>
                      <span>المبتكرون</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <i className="ri-check-line text-[#4FACFE]"></i>
                      <span>مطورو التقنية</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. الفكرة */}
              <div>
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-gradient-to-br from-[#4FACFE] to-[#00A0E3] rounded-xl flex items-center justify-center text-white">2</span>
                  الفكرة
                </h4>
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-xl border border-blue-100">
                  <p className="text-lg text-gray-800 font-semibold mb-4">ZINC ليست مجرد مساحة عمل، بل:</p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#4FACFE] text-xl mt-1"></i>
                      <span className="text-gray-700">منصة وطنية لدعم الابتكار</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#4FACFE] text-xl mt-1"></i>
                      <span className="text-gray-700">حاضنة أفكار ومشاريع</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#4FACFE] text-xl mt-1"></i>
                      <span className="text-gray-700">جسر يربط الشباب بالجهات</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#4FACFE] text-xl mt-1"></i>
                      <span className="text-gray-700">نظام بيئي متكامل لريادة الأعمال</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. مكونات نموذج ZINC */}
              <div>
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-gradient-to-br from-[#4FACFE] to-[#00A0E3] rounded-xl flex items-center justify-center text-white">3</span>
                  مكونات نموذج ZINC
                </h4>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white p-6 rounded-xl border-2 border-blue-100 hover:border-blue-300 transition-all">
                    <h5 className="font-bold text-lg text-[#4FACFE] mb-4">أولًا: المساحات</h5>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-[#4FACFE] mt-1"></i>
                        <span>مساحات عمل مشتركة</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-[#4FACFE] mt-1"></i>
                        <span>قاعات تدريب</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-[#4FACFE] mt-1"></i>
                        <span>غرف اجتماعات</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-[#4FACFE] mt-1"></i>
                        <span>مناطق عرض المشاريع</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-xl border-2 border-blue-100 hover:border-blue-300 transition-all">
                    <h5 className="font-bold text-lg text-[#4FACFE] mb-4">ثانيًا: البرامج</h5>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-[#4FACFE] mt-1"></i>
                        <span>برامج احتضان وتسريع</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-[#4FACFE] mt-1"></i>
                        <span>ورش ريادة أعمال</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-[#4FACFE] mt-1"></i>
                        <span>تدريب مهني وتقني</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-[#4FACFE] mt-1"></i>
                        <span>جلسات إرشاد Mentorship</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-xl border-2 border-blue-100 hover:border-blue-300 transition-all">
                    <h5 className="font-bold text-lg text-[#4FACFE] mb-4">ثالثًا: المجتمع</h5>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-[#4FACFE] mt-1"></i>
                        <span>شبكة خبراء ومستثمرين</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-[#4FACFE] mt-1"></i>
                        <span>شراكات جامعية</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-[#4FACFE] mt-1"></i>
                        <span>فعاليات ابتكار</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-[#4FACFE] mt-1"></i>
                        <span>مسابقات وريادة أعمال</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 4. نموذج التمويل */}
              <div>
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-gradient-to-br from-[#4FACFE] to-[#00A0E3] rounded-xl flex items-center justify-center text-white">4</span>
                  نموذج التمويل
                </h4>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
                      <span className="text-gray-700">تمويل مباشر من شركة زين</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
                      <span className="text-gray-700">رعايات شركات أخرى</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
                      <span className="text-gray-700">شراكات استراتيجية</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
                      <span className="text-gray-700">برامج مدعومة من جهات مانحة</span>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg text-center">
                    <p className="font-bold text-lg text-green-700">نموذج تمويل مؤسسي طويل المدى</p>
                  </div>
                </div>
              </div>

              {/* 5. نقاط القوة */}
              <div>
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center text-white">5</span>
                  نقاط القوة
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    'دعم قوي من شركة كبرى',
                    'وضوح الرسالة التنموية',
                    'برامج احترافية',
                    'شبكة علاقات قوية',
                    'ثقة عالية من المجتمع الريادي',
                    'استدامة مالية أفضل من الحاضنات التقليدية'
                  ].map((strength, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-green-50 p-4 rounded-lg border border-green-200">
                      <i className="ri-check-double-line text-2xl text-green-600"></i>
                      <span className="text-gray-700">{strength}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 6. نقاط الضعف */}
              <div>
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center text-white">6</span>
                  نقاط الضعف
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    'الاعتماد الكبير على شركة واحدة',
                    'محدودية الانتشار خارج الأردن',
                    'قد يتأثر بتغير استراتيجية الشركة',
                    'بعض البرامج موسمية'
                  ].map((weakness, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-orange-50 p-4 rounded-lg border border-orange-200">
                      <i className="ri-error-warning-line text-2xl text-orange-600"></i>
                      <span className="text-gray-700">{weakness}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 7. ما الذي نستفيده من ZINC */}
              <div>
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] rounded-xl flex items-center justify-center text-white">7</span>
                  ما الذي نستفيده من ZINC؟
                </h4>
                <div className="bg-gradient-to-br from-[#FF6B6B]/10 to-[#FF8E53]/10 p-8 rounded-xl border-2 border-[#FF6B6B]/30">
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      'قوة الشراكة مع شركة كبرى',
                      'أهمية البرامج وليس المكان فقط',
                      'دمج التدريب مع المساحة',
                      'بناء منظومة متكاملة',
                      'قياس الأثر الاجتماعي',
                      'صناعة قصص نجاح حقيقية'
                    ].map((lesson, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm">
                        <i className="ri-lightbulb-flash-line text-2xl text-[#FF6B6B]"></i>
                        <span className="text-gray-700 font-semibold">{lesson}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>

          {/* WeWork Accordion */}
          <AccordionItem
            title="WeWork – الولايات المتحدة"
            number={2}
            color="bg-gradient-to-br from-purple-500 to-purple-600"
            isOpen={openAccordion === 'wework'}
            onToggle={() => toggleAccordion('wework')}
          >
            <div className="space-y-8">
              {/* Hero Image */}
              <div className="w-full h-80 rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-gray-100 to-gray-200">
                <img
                  src="/WeWork1png.png"
                  alt="WeWork"
                  className="w-full h-full object-cover object-center"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1200&h=400&fit=crop&q=80';
                  }}
                />
              </div>

              {/* Overview */}
              <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-200">
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <i className="ri-information-line text-[#FF6B6B]"></i>
                  نبذة عامة
                </h4>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FF6B6B]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-calendar-line text-[#FF6B6B] text-xl"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">التأسيس</p>
                      <p className="text-gray-600">2010 – نيويورك</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FF6B6B]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-team-line text-[#FF6B6B] text-xl"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">المؤسسون</p>
                      <p className="text-gray-600">Adam Neumann – Miguel McKelvey</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FF6B6B]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-[#FF6B6B] text-xl"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">الانتشار</p>
                      <p className="text-gray-600">أكثر من 700 موقع في أكثر من 35 دولة</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FF6B6B]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-bar-chart-line text-[#FF6B6B] text-xl"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">النموذج</p>
                      <p className="text-gray-600">ربحي – Commercial Coworking</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 p-6 bg-white rounded-xl border-r-4 border-[#FF6B6B]">
                  <p className="text-gray-700 text-lg leading-relaxed">
                    WeWork أعادت تعريف مفهوم المكتب، وحوّلته من مساحة صامتة إلى مجتمع عمل نابض بالحياة.
                  </p>
                </div>
              </div>

              {/* Core Idea */}
              <div className="bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] p-8 rounded-2xl text-white">
                <h4 className="font-bold text-2xl mb-6 flex items-center gap-3">
                  <i className="ri-lightbulb-line"></i>
                  الفكرة الجوهرية
                </h4>
                <p className="text-3xl font-bold mb-6">"المكتب ليس مكانًا، بل تجربة"</p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="font-semibold mb-3 text-lg">WeWork لا تبيع:</p>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <i className="ri-close-circle-line"></i>
                        مكتب
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-close-circle-line"></i>
                        إنترنت
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-close-circle-line"></i>
                        طاولة
                      </li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold mb-3 text-lg">بل تبيع:</p>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line"></i>
                        انتماء
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line"></i>
                        شبكة علاقات
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line"></i>
                        مرونة
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line"></i>
                        هوية مهنية
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Components */}
              <div>
                <h4 className="font-bold text-2xl text-gray-900 mb-6">مكونات نموذج WeWork</h4>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="w-14 h-14 bg-[#FF6B6B]/10 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-building-line text-[#FF6B6B] text-2xl"></i>
                    </div>
                    <h5 className="font-bold text-xl text-gray-900 mb-4">المساحات</h5>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        مكاتب مفتوحة
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        مكاتب خاصة
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        قاعات اجتماعات
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        مساحات فعاليات
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="w-14 h-14 bg-[#FF6B6B]/10 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-service-line text-[#FF6B6B] text-2xl"></i>
                    </div>
                    <h5 className="font-bold text-xl text-gray-900 mb-4">الخدمات</h5>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        إنترنت عالي السرعة
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        استقبال
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        طباعة
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        فعاليات مجتمعية
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        تطبيق رقمي
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="w-14 h-14 bg-[#FF6B6B]/10 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-group-line text-[#FF6B6B] text-2xl"></i>
                    </div>
                    <h5 className="font-bold text-xl text-gray-900 mb-4">المجتمع</h5>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        لقاءات Networking
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        فعاليات أسبوعية
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        دعم المشاريع
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        تفاعل بين الأعضاء
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Revenue Model */}
              <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-200">
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <i className="ri-money-dollar-circle-line text-[#FF6B6B]"></i>
                  نموذج الإيرادات
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 bg-white p-4 rounded-xl">
                    <i className="ri-calendar-check-line text-[#FF6B6B] text-xl"></i>
                    <span className="text-gray-700">اشتراكات شهرية</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-4 rounded-xl">
                    <i className="ri-file-text-line text-[#FF6B6B] text-xl"></i>
                    <span className="text-gray-700">عقود طويلة مع شركات</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-4 rounded-xl">
                    <i className="ri-door-open-line text-[#FF6B6B] text-xl"></i>
                    <span className="text-gray-700">تأجير قاعات</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-4 rounded-xl">
                    <i className="ri-add-circle-line text-[#FF6B6B] text-xl"></i>
                    <span className="text-gray-700">خدمات إضافية</span>
                  </div>
                </div>
              </div>

              {/* Strengths & Weaknesses */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 p-6 rounded-2xl border border-green-200">
                  <h4 className="font-bold text-xl text-green-900 mb-4 flex items-center gap-2">
                    <i className="ri-thumb-up-line"></i>
                    نقاط القوة
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-green-800">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-1"></i>
                      <span>تجربة مستخدم قوية</span>
                    </li>
                    <li className="flex items-start gap-2 text-green-800">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-1"></i>
                      <span>تصميم جذاب</span>
                    </li>
                    <li className="flex items-start gap-2 text-green-800">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-1"></i>
                      <span>شبكة علاقات عالمية</span>
                    </li>
                    <li className="flex items-start gap-2 text-green-800">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-1"></i>
                      <span>علامة تجارية مؤثرة</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-red-50 p-6 rounded-2xl border border-red-200">
                  <h4 className="font-bold text-xl text-red-900 mb-4 flex items-center gap-2">
                    <i className="ri-thumb-down-line"></i>
                    نقاط الضعف (دروس مستفادة)
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-red-800">
                      <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                      <span>تكلفة تشغيل عالية</span>
                    </li>
                    <li className="flex items-start gap-2 text-red-800">
                      <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                      <span>توسع أسرع من الاستدامة</span>
                    </li>
                    <li className="flex items-start gap-2 text-red-800">
                      <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                      <span>اعتماد كبير على الإيجارات</span>
                    </li>
                    <li className="flex items-start gap-2 text-red-800">
                      <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                      <span>تجاهل البعد الاجتماعي العميق</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Key Insight */}
              <div className="bg-gradient-to-l from-gray-900 to-gray-800 p-8 rounded-2xl text-white">
                <p className="text-center text-2xl font-bold mb-4">وهنا الفارق الجوهري:</p>
                <p className="text-center text-xl">
                  WeWork = مساحة عمل ممتازة
                  <br />
                  <span className="text-[#FF6B6B]">لكنها ليست منصة أثر اجتماعي حقيقي</span>
                </p>
              </div>

              {/* Lessons */}
              <div className="bg-gradient-to-br from-[#FF6B6B]/10 to-[#FF8E53]/10 p-8 rounded-2xl border border-[#FF6B6B]/20">
                <h4 className="font-bold text-2xl text-gray-900 mb-6">ما الذي تتعلمه من WeWork لمشروعك؟</h4>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="font-semibold text-gray-900 mb-3">تعلّم:</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-gray-700">
                        <i className="ri-star-fill text-[#FF6B6B] mt-1"></i>
                        <span>قوة التصميم والتجربة</span>
                      </li>
                      <li className="flex items-start gap-2 text-gray-700">
                        <i className="ri-star-fill text-[#FF6B6B] mt-1"></i>
                        <span>بناء مجتمع حقيقي</span>
                      </li>
                      <li className="flex items-start gap-2 text-gray-700">
                        <i className="ri-star-fill text-[#FF6B6B] mt-1"></i>
                        <span>أهمية العلامة التجارية</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-3">لكن تجنّب:</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-gray-700">
                        <i className="ri-alert-fill text-red-500 mt-1"></i>
                        <span>التوسع المفرط</span>
                      </li>
                      <li className="flex items-start gap-2 text-gray-700">
                        <i className="ri-alert-fill text-red-500 mt-1"></i>
                        <span>النمو دون شراكات مستدامة</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>

          {/* Impact Hub Accordion */}
          <AccordionItem
            title="Impact Hub – شبكة عالمية"
            number={3}
            color="bg-gradient-to-br from-green-500 to-green-600"
            isOpen={openAccordion === 'impacthub'}
            onToggle={() => toggleAccordion('impacthub')}
          >
            <div className="space-y-8">
              {/* Hero Image */}
              <div className="w-full h-80 rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-gray-100 to-gray-200">
                <img
                  src="/ImpactHub1.png"
                  alt="Impact Hub"
                  className="w-full h-full object-cover object-center"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/ImpactHub2.jpg';
                  }}
                />
              </div>

              {/* Overview */}
              <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-200">
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <i className="ri-information-line text-[#FF6B6B]"></i>
                  نبذة عامة
                </h4>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FF6B6B]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-calendar-line text-[#FF6B6B] text-xl"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">التأسيس</p>
                      <p className="text-gray-600">لندن - 2005</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FF6B6B]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-[#FF6B6B] text-xl"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">الانتشار</p>
                      <p className="text-gray-600">+100 مدينة حول العالم</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FF6B6B]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-bar-chart-line text-[#FF6B6B] text-xl"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">النموذج</p>
                      <p className="text-gray-600">اجتماعي – شبكي – غير ربحي / هجين</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FF6B6B]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-heart-line text-[#FF6B6B] text-xl"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">الفئة المستهدفة</p>
                      <p className="text-gray-600">رواد الأعمال الاجتماعيين، المشاريع ذات الأثر، المبادرات المجتمعية</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Core Idea */}
              <div className="bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] p-8 rounded-2xl text-white">
                <h4 className="font-bold text-2xl mb-6 flex items-center gap-3">
                  <i className="ri-lightbulb-line"></i>
                  الفكرة الجوهرية
                </h4>
                <p className="text-3xl font-bold mb-6">مساحة العمل كأداة لتغيير العالم</p>
                <p className="text-xl mb-4">Impact Hub ليست مجرد Coworking، بل:</p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                    <i className="ri-rocket-line text-3xl mb-2"></i>
                    <p className="font-semibold">منصة ابتكار</p>
                  </div>
                  <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                    <i className="ri-flask-line text-3xl mb-2"></i>
                    <p className="font-semibold">مختبر حلول</p>
                  </div>
                  <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                    <i className="ri-team-line text-3xl mb-2"></i>
                    <p className="font-semibold">مجتمع قادة تغيير</p>
                  </div>
                </div>
              </div>

              {/* Components */}
              <div>
                <h4 className="font-bold text-2xl text-gray-900 mb-6">مكونات نموذج Impact Hub</h4>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="w-14 h-14 bg-[#FF6B6B]/10 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-building-line text-[#FF6B6B] text-2xl"></i>
                    </div>
                    <h5 className="font-bold text-xl text-gray-900 mb-4">المساحة</h5>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        مساحات عمل مشتركة
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        قاعات تدريب
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        مختبرات ابتكار
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="w-14 h-14 bg-[#FF6B6B]/10 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-code-box-line text-[#FF6B6B] text-2xl"></i>
                    </div>
                    <h5 className="font-bold text-xl text-gray-900 mb-4">البرامج</h5>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        احتضان مشاريع
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        تسريع أعمال
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        برامج أثر اجتماعي
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        شراكات مع جهات مانحة
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="w-14 h-14 bg-[#FF6B6B]/10 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-global-line text-[#FF6B6B] text-2xl"></i>
                    </div>
                    <h5 className="font-bold text-xl text-gray-900 mb-4">المجتمع</h5>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        مجتمع عالمي مترابط
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        تبادل خبرات
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-arrow-left-s-line text-[#FF6B6B]"></i>
                        دعم عابر للحدود
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Funding Model */}
              <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-200">
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <i className="ri-money-dollar-circle-line text-[#FF6B6B]"></i>
                  نموذج التمويل
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 bg-white p-4 rounded-xl">
                    <i className="ri-price-tag-3-line text-[#FF6B6B] text-xl"></i>
                    <span className="text-gray-700">اشتراكات رمزية</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-4 rounded-xl">
                    <i className="ri-gift-line text-[#FF6B6B] text-xl"></i>
                    <span className="text-gray-700">منح</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-4 rounded-xl">
                    <i className="ri-user-heart-line text-[#FF6B6B] text-xl"></i>
                    <span className="text-gray-700">شراكات مؤسسية</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-4 rounded-xl">
                    <i className="ri-funds-line text-[#FF6B6B] text-xl"></i>
                    <span className="text-gray-700">برامج ممولة</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-4 rounded-xl">
                    <i className="ri-government-line text-[#FF6B6B] text-xl"></i>
                    <span className="text-gray-700">دعم حكومي أحيانًا</span>
                  </div>
                </div>
              </div>

              {/* Strengths & Weaknesses */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 p-6 rounded-2xl border border-green-200">
                  <h4 className="font-bold text-xl text-green-900 mb-4 flex items-center gap-2">
                    <i className="ri-thumb-up-line"></i>
                    نقاط القوة
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-green-800">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-1"></i>
                      <span>وضوح الرسالة</span>
                    </li>
                    <li className="flex items-start gap-2 text-green-800">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-1"></i>
                      <span>أثر اجتماعي ملموس</span>
                    </li>
                    <li className="flex items-start gap-2 text-green-800">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-1"></i>
                      <span>شبكة عالمية</span>
                    </li>
                    <li className="flex items-start gap-2 text-green-800">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-1"></i>
                      <span>ثقة الجهات المانحة</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-red-50 p-6 rounded-2xl border border-red-200">
                  <h4 className="font-bold text-xl text-red-900 mb-4 flex items-center gap-2">
                    <i className="ri-thumb-down-line"></i>
                    نقاط الضعف
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-red-800">
                      <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                      <span>أقل جاذبية تجاريًا</span>
                    </li>
                    <li className="flex items-start gap-2 text-red-800">
                      <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                      <span>أحيانًا ضعف الاستدامة المالية</span>
                    </li>
                    <li className="flex items-start gap-2 text-red-800">
                      <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                      <span>اعتماد على التمويل الخارجي</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Lessons */}
              <div className="bg-gradient-to-br from-[#FF6B6B]/10 to-[#FF8E53]/10 p-8 rounded-2xl border border-[#FF6B6B]/20">
                <h4 className="font-bold text-2xl text-gray-900 mb-6">ما الذي تتعلمه من Impact Hub؟</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 bg-white p-4 rounded-xl">
                    <i className="ri-star-fill text-[#FF6B6B] text-xl mt-1"></i>
                    <span className="text-gray-700">ربط المساحة بالرسالة</span>
                  </div>
                  <div className="flex items-start gap-3 bg-white p-4 rounded-xl">
                    <i className="ri-star-fill text-[#FF6B6B] text-xl mt-1"></i>
                    <span className="text-gray-700">قياس الأثر</span>
                  </div>
                  <div className="flex items-start gap-3 bg-white p-4 rounded-xl">
                    <i className="ri-star-fill text-[#FF6B6B] text-xl mt-1"></i>
                    <span className="text-gray-700">الشراكات الذكية</span>
                  </div>
                  <div className="flex items-start gap-3 bg-white p-4 rounded-xl">
                    <i className="ri-star-fill text-[#FF6B6B] text-xl mt-1"></i>
                    <span className="text-gray-700">بناء مجتمع هادف</span>
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>

          {/* White Space Accordion */}
          <AccordionItem
            title="White Space – السعودية"
            number={4}
            color="bg-gradient-to-br from-emerald-500 to-green-500"
            isOpen={openAccordion === 'whitespace'}
            onToggle={() => toggleAccordion('whitespace')}
          >
            <div className="space-y-8">
              {/* Hero Image */}
              <div className="w-full h-80 rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-gray-100 to-gray-200">
                <img
                  src="/WhiteSpace1.png"
                  alt="White Space Saudi Arabia"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1200&h=400&fit=crop&q=80';
                  }}
                />
              </div>

              {/* 1. نبذة عامة */}
              <div>
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center text-white">1</span>
                  نبذة عامة
                </h4>
                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-6 rounded-xl border border-emerald-100">
                    <div className="text-sm font-semibold text-gray-700 mb-2">التأسيس</div>
                    <div className="text-lg font-bold text-gray-900">تقريبًا 2017</div>
                    <div className="text-sm text-gray-600">السعودية</div>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-6 rounded-xl border border-emerald-100">
                    <div className="text-sm font-semibold text-gray-700 mb-2">الانتشار</div>
                    <div className="text-lg font-bold text-gray-900">3 مدن رئيسية</div>
                    <div className="text-sm text-gray-600">الرياض – جدة – الخبر</div>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-6 rounded-xl border border-emerald-100">
                    <div className="text-sm font-semibold text-gray-700 mb-2">النموذج</div>
                    <div className="text-lg font-bold text-gray-900">Commercial</div>
                    <div className="text-sm text-gray-600">مساحة عمل مشتركة تجارية</div>
                  </div>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <h5 className="font-bold text-lg text-gray-900 mb-4">الفئة المستهدفة:</h5>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 text-gray-700">
                      <i className="ri-check-line text-emerald-600"></i>
                      <span>رواد الأعمال</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <i className="ri-check-line text-emerald-600"></i>
                      <span>المستقلون</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <i className="ri-check-line text-emerald-600"></i>
                      <span>الجهات الصغيرة والمتوسطة</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <i className="ri-check-line text-emerald-600"></i>
                      <span>فرق العمل الناشئة</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. الفكرة */}
              <div>
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center text-white">2</span>
                  الفكرة
                </h4>
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-8 rounded-xl border border-emerald-100">
                  <p className="text-lg text-gray-800 font-semibold mb-4">White Space ليست مجرد مساحة عمل، بل:</p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1"></i>
                      <span className="text-gray-700">بيئة احترافية جاهزة للأعمال</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1"></i>
                      <span className="text-gray-700">بديل مرن للمكاتب التقليدية</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1"></i>
                      <span className="text-gray-700">منصة تجمع محترفي الأعمال تحت سقف واحد</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1"></i>
                      <span className="text-gray-700">حل عملي للشركات الناشئة بدل استئجار مكاتب طويلة الأجل</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. مكونات نموذج White Space */}
              <div>
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center text-white">3</span>
                  مكونات نموذج White Space
                </h4>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white p-6 rounded-xl border-2 border-emerald-100 hover:border-emerald-300 transition-all">
                    <h5 className="font-bold text-lg text-emerald-600 mb-4">أولًا: المساحات</h5>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-emerald-600 mt-1"></i>
                        <span>مكاتب مشتركة (Hot Desk)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-emerald-600 mt-1"></i>
                        <span>مكاتب ثابتة (Dedicated Desk)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-emerald-600 mt-1"></i>
                        <span>مكاتب خاصة للشركات</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-emerald-600 mt-1"></i>
                        <span>غرف اجتماعات</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-emerald-600 mt-1"></i>
                        <span>قاعات فعاليات</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-xl border-2 border-emerald-100 hover:border-emerald-300 transition-all">
                    <h5 className="font-bold text-lg text-emerald-600 mb-4">ثانيًا: الخدمات</h5>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-emerald-600 mt-1"></i>
                        <span>إنترنت عالي السرعة</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-emerald-600 mt-1"></i>
                        <span>استقبال وضيافة</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-emerald-600 mt-1"></i>
                        <span>طباعة وخدمات مكتبية</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-emerald-600 mt-1"></i>
                        <span>حجز غرف عبر نظام رقمي</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-emerald-600 mt-1"></i>
                        <span>صيانة ونظافة مستمرة</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-xl border-2 border-emerald-100 hover:border-emerald-300 transition-all">
                    <h5 className="font-bold text-lg text-emerald-600 mb-4">ثالثًا: المجتمع</h5>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-emerald-600 mt-1"></i>
                        <span>فعاليات Networking</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-emerald-600 mt-1"></i>
                        <span>لقاءات أعمال</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-left-s-line text-emerald-600 mt-1"></i>
                        <span>ورش بسيطة لرواد الأعمال</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 4. نموذج التمويل */}
              <div>
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center text-white">4</span>
                  نموذج التمويل
                </h4>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
                      <span className="text-gray-700">اشتراكات شهرية وسنوية</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
                      <span className="text-gray-700">تأجير المكاتب الخاصة</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
                      <span className="text-gray-700">تأجير القاعات بالساعة</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
                      <span className="text-gray-700">خدمات إضافية مدفوعة</span>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg text-center">
                    <p className="font-bold text-lg text-green-700">نموذج ربحي بحت</p>
                  </div>
                </div>
              </div>

              {/* 5. نقاط القوة */}
              <div>
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center text-white">5</span>
                  نقاط القوة
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    'علامة تجارية محلية معروفة',
                    'مواقع استراتيجية في مدن رئيسية',
                    'تصميم عصري وجذاب',
                    'مرونة عالية في العقود',
                    'جاهزية تشغيلية عالية'
                  ].map((strength, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-green-50 p-4 rounded-lg border border-green-200">
                      <i className="ri-check-double-line text-2xl text-green-600"></i>
                      <span className="text-gray-700">{strength}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 6. نقاط الضعف */}
              <div>
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center text-white">6</span>
                  نقاط الضعف
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    'تركيز تجاري أكثر من تنموي',
                    'برامج تطوير محدودة',
                    'ضعف الربط مع الجامعات',
                    'لا تستهدف الطلاب بشكل مباشر',
                    'المنافسة السعرية العالية'
                  ].map((weakness, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-orange-50 p-4 rounded-lg border border-orange-200">
                      <i className="ri-error-warning-line text-2xl text-orange-600"></i>
                      <span className="text-gray-700">{weakness}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 7. ما الذي نستفيده من White Space */}
              <div>
                <h4 className="font-bold text-2xl text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] rounded-xl flex items-center justify-center text-white">7</span>
                  ما الذي نستفيده من White Space؟
                </h4>
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-8 rounded-xl border-2 border-emerald-200">
                  <div className="mb-6">
                    <h5 className="font-bold text-lg text-gray-900 mb-4">الدروس المستفادة:</h5>
                    <div className="grid md:grid-cols-2 gap-4">
                      {[
                        'أهمية الموقع الاستراتيجي',
                        'جودة التصميم وتجربة المستخدم',
                        'تنويع خيارات الاشتراك',
                        'بناء علامة محلية قوية'
                      ].map((lesson, idx) => (
                        <div key={idx} className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm">
                          <i className="ri-lightbulb-flash-line text-2xl text-emerald-600"></i>
                          <span className="text-gray-700 font-semibold">{lesson}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-orange-50 p-6 rounded-xl border-2 border-orange-200">
                    <h5 className="font-bold text-lg text-orange-700 mb-4">لكن يجب الانتباه:</h5>
                    <div className="space-y-3">
                      {[
                        'لا تعتمد فقط على الاشتراكات',
                        'أضف بُعدًا تدريبيًا وتمكينيًا',
                        'ركّز على الطلاب والشباب'
                      ].map((warning, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <i className="ri-alert-line text-xl text-orange-600"></i>
                          <span className="text-gray-700">{warning}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>
        </div>

        {/* Comparison Table */}
        <div className="mb-16 mt-16">
          <h3 className="font-bold text-3xl text-gray-900 mb-8 text-center">جدول المقارنة بين النماذج العالمية الأربعة</h3>
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xl mt-12">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white p-8">
              <h3 className="font-bold text-3xl text-center">المقارنة الجوهرية بين النماذج</h3>
            </div>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-l from-gray-100 to-gray-50">
                    <th className="p-6 text-right font-bold text-gray-900 border-l border-gray-200">م</th>
                    <th className="p-6 text-right font-bold text-gray-900 border-l border-gray-200">الجهة</th>
                    <th className="p-6 text-right font-bold text-gray-900 border-l border-gray-200">الامتيازات</th>
                    <th className="p-6 text-right font-bold text-gray-900 border-l border-gray-200">التحسينات</th>
                    <th className="p-6 text-center font-bold text-gray-900">الموقع</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                    <td className="p-6 text-center font-semibold text-gray-900 border-l border-gray-200">1</td>
                    <td className="p-6 text-right font-semibold text-gray-900 border-l border-gray-200">WeWork</td>
                    <td className="p-6 text-right text-gray-700 border-l border-gray-200">مساحة عمل عالمية احترافية تجارية ذات بنية قوية وخبرة تصميم عالية</td>
                    <td className="p-6 text-right text-gray-700 border-l border-gray-200">تتجاهل غالبًا البعد التنموي للطلاب ورواد الأعمال</td>
                    <td className="p-6 text-center"><a href="https://www.wework.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">WeWork</a></td>
                  </tr>
                  <tr className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                    <td className="p-6 text-center font-semibold text-gray-900 border-l border-gray-200">2</td>
                    <td className="p-6 text-right font-semibold text-gray-900 border-l border-gray-200">Impact Hub</td>
                    <td className="p-6 text-right text-gray-700 border-l border-gray-200">مساحة تركز على الأثر الاجتماعي وتطوير رواد الأعمال</td>
                    <td className="p-6 text-right text-gray-700 border-l border-gray-200">تواجه تحديات في الاستدامة المالية</td>
                    <td className="p-6 text-center"><a href="https://impacthub.net/ar/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">Impact Hub</a></td>
                  </tr>
                  <tr className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                    <td className="p-6 text-center font-semibold text-gray-900 border-l border-gray-200">3</td>
                    <td className="p-6 text-right font-semibold text-gray-900 border-l border-gray-200">White Space السعودية</td>
                    <td className="p-6 text-right text-gray-700 border-l border-gray-200">نموذج محلي احترافي يقدم بيئة مرنة ومرافق جيدة</td>
                    <td className="p-6 text-right text-gray-700 border-l border-gray-200">يركز أكثر على المحترفين والجهات الصغيرة دون دمج برامج تمكينية قوية</td>
                    <td className="p-6 text-center"><a href="https://whitespace.sa" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">WhiteSpace</a></td>
                  </tr>
                  <tr className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                    <td className="p-6 text-center font-semibold text-gray-900 border-l border-gray-200">4</td>
                    <td className="p-6 text-right font-semibold text-gray-900 border-l border-gray-200">Zain Innovation Campus<br/>(ZINC – الأردن)</td>
                    <td className="p-6 text-right text-gray-700 border-l border-gray-200">منصة تدعم الابتكار وريادة الأعمال من خلال مساحة عمل + دعم تكويني + شبكة علاقات</td>
                    <td className="p-6 text-right text-gray-700 border-l border-gray-200">مما يجعله نموذجًا قويًا لدمج العمل والتمكين</td>
                    <td className="p-6 text-center"><a href="https://zinc.jo" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">zinc.jo</a></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {[
                { num: 1, name: 'WeWork', pros: 'مساحة عمل عالمية احترافية تجارية ذات بنية قوية وخبرة تصميم عالية', cons: 'تتجاهل غالبًا البعد التنموي للطلاب ورواد الأعمال', url: 'https://www.wework.com', label: 'WeWork' },
                { num: 2, name: 'Impact Hub', pros: 'مساحة تركز على الأثر الاجتماعي وتطوير رواد الأعمال', cons: 'تواجه تحديات في الاستدامة المالية', url: 'https://impacthub.net/ar/', label: 'Impact Hub' },
                { num: 3, name: 'White Space السعودية', pros: 'نموذج محلي احترافي يقدم بيئة مرنة ومرافق جيدة', cons: 'يركز أكثر على المحترفين والجهات الصغيرة دون دمج برامج تمكينية قوية', url: 'https://whitespace.sa', label: 'WhiteSpace' },
                { num: 4, name: 'ZINC – الأردن', pros: 'منصة تدعم الابتكار وريادة الأعمال من خلال مساحة عمل + دعم تكويني + شبكة علاقات', cons: 'مما يجعله نموذجًا قويًا لدمج العمل والتمكين', url: 'https://zinc.jo', label: 'zinc.jo' },
              ].map((item) => (
                <div key={item.num} className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="w-7 h-7 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{item.num}</span>
                    <span className="font-bold text-gray-900 text-base">{item.name}</span>
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-xs underline">{item.label}</a>
                  </div>
                  <div className="bg-green-50 border border-green-100 rounded-xl p-3 mb-2">
                    <p className="text-xs font-semibold text-green-700 mb-1">✅ الامتيازات</p>
                    <p className="text-sm text-gray-700">{item.pros}</p>
                  </div>
                  <div className="bg-orange-50 border border-orange-100 rounded-xl p-3">
                    <p className="text-xs font-semibold text-orange-700 mb-1">⚡ التحسينات</p>
                    <p className="text-sm text-gray-700">{item.cons}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Conclusion */}
        <div className="mt-16 bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] text-white rounded-2xl p-12 shadow-2xl">
          <div className="text-center mb-8">
            <i className="ri-lightbulb-flash-line text-6xl mb-4"></i>
            <h3 className="font-bold text-4xl mb-4">الخلاصة</h3>
            <p className="text-xl opacity-90">مشروعنا لا يكرر ولا ينسخ</p>
          </div>
          <p className="text-2xl font-bold text-center mb-8">بل يمزج أفضل ما في:</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm border border-white/20 text-center">
              <i className="ri-star-line text-4xl mb-3"></i>
              <p className="font-semibold text-lg">تجربة احترافية جذابة</p>
            </div>
            <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm border border-white/20 text-center">
              <i className="ri-lightbulb-line text-4xl mb-3"></i>
              <p className="font-semibold text-lg">رسالة واضحة</p>
            </div>
            <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm border border-white/20 text-center">
              <i className="ri-checkbox-circle-line text-4xl mb-3"></i>
              <p className="font-semibold text-lg">أثر قابل للقياس</p>
            </div>
            <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm border border-white/20 text-center">
              <i className="ri-refresh-line text-4xl mb-3"></i>
              <p className="font-semibold text-lg">استدامة عبر شراكات الجهات الكبرى</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
